import React, {
  Suspense, lazy, useRef, useState, useContext, useEffect, useCallback
} from 'react';
import { bool, func, number, string } from 'prop-types';
import { RatingMeter, Button, Skeleton, SkeletonBlock } from '@one-thd/sui-atomic-components';
import { ArrowDown } from '@one-thd/sui-icons';
import { extend, useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { smoothScroll, getProductObject, getReviewInfo } from '../../productDetailsUtils';
import * as analytics from '../../analytics';
import { ProductDetailsContext } from '../ProductDetailsContext';
import { dataModel } from './ratings-and-reviews-data-model';
import { RatingsPopover } from './RatingsPopover/RatingsPopover.component';

const ProductDetailsRatings = (props) => {
  const {
    offsetselector, qaselector, pageType, target, totalReviewCount, hideRatingsIfHDPPSku, targetURL, showBadge,
    disableReviewsIfZero, onClick
  } = props;
  const {
    product, itemId, channel
  } = useContext(ProductDetailsContext);

  const [showPopover, setShowPopover] = useState(false);
  const ratingsRef = useRef();

  const { data: reviewData, loading: reviewLoading, error: reviewError } = useDataModel('reviews', {
    variables: {
      itemId,
      filters: {
        isVerifiedPurchase: false,
        prosCons: null,
        starRatings: null
      },
      searchTerm: null,
      sortBy: 'photoreview',
      startIndex: 1
    }
  });

  const { data: qaData, loading: qaLoading, error: qaError } = useDataModel('questionsAnswers', {
    variables: {
      itemId,
      pagesize: 4,
      searchTerm: '',
      sortBy: 'newest',
      startindex: 1
    }
  });

  const analyticsData = (element) => {
    analytics.track('click', {
      element
    });
  };

  if (reviewLoading || !product) {
    return (
      <Skeleton disablePadding>
        <SkeletonBlock height={8} width={32} />
      </Skeleton>
    );
  }

  const openPopover = () => {
    setShowPopover(true);
  };

  const closePopover = () => {
    setShowPopover(false);
  };

  const handleRatingsClick = (event) => {
    smoothScroll(event, target, offsetselector);
    onClick(target);
    analyticsData('star rating');
  };

  const handleQuestionAnswerClick = (event) => {
    smoothScroll(event, qaselector, offsetselector);
    onClick(qaselector);
    analyticsData('questions and answers');
  };

  const itemObj = getProductObject(reviewData?.reviews, reviewData?.reviews.TotalResults);
  const { averageRating, ratingsTitle } = getReviewInfo(itemObj, product, reviewData?.reviews.TotalResults);
  const { skuClassification } = product?.identifiers || {};

  const isHiddenHDPPSku = (hideRatingsIfHDPPSku && skuClassification === 'hdpp');
  const isHiddenZeroReviews = (disableReviewsIfZero && reviewData?.reviews.TotalResults === 0);

  return (
    <div className="sui-flex sui-flex-row sui-items-center" data-component="ProductDetailsRatings">
      { !isHiddenHDPPSku
      && !isHiddenZeroReviews
       && (
         <>
           <a
             className="sui-flex sui-flex-row sui-items-center"
             onClick={handleRatingsClick}
             onMouseEnter={openPopover}
             onFocus={openPopover}
             onBlur={closePopover}
             href={target}
             title={ratingsTitle}
             ref={ratingsRef}
           >
             <span
               id="product-details__review__target"
               className="sui--ml-2"
             >
               <RatingMeter
                 value={averageRating}
                 label={averageRating !== 0 ? `(${reviewData?.reviews.TotalResults})` : ''}
               />
             </span>
             {showBadge && (
               <ArrowDown size="small" />
             )}
           </a>
           {showBadge && channel === 'desktop'
            && ((!reviewError) && reviewData?.reviews)
             && ratingsRef.current
             && (
               <RatingsPopover
                 open={showPopover}
                 anchorEl={ratingsRef.current}
                 onClick={handleRatingsClick}
                 onClose={closePopover}
                 reviewStats={reviewData?.reviews}
               />
             )}
           {showBadge && channel === 'desktop'
      && (!(qaLoading || qaError) && qaData)
        && (
          <Button
            onClick={handleQuestionAnswerClick}
            className="sui-ml-2 sui-cursor-pointer hover:sui-text-underline"
            variant="ghost"
            href={qaselector || targetURL}
          >
            Questions & Answers&nbsp;
            {qaData.questionsAnswers.TotalResults ? `(${qaData.questionsAnswers.TotalResults})` : ''}
          </Button>
        )}
         </>
       )}
    </div>
  );
};

ProductDetailsRatings.propTypes = {
  offsetselector: string,
  pageType: string,
  qaselector: string,
  showBadge: bool,
  target: string,
  totalReviewCount: number,
  disableReviewsIfZero: bool,
  onClick: func,
  hideRatingsIfHDPPSku: bool,
  targetURL: string
};

ProductDetailsRatings.defaultProps = {
  offsetselector: null,
  pageType: 'PIP',
  qaselector: null,
  showBadge: false,
  target: null,
  totalReviewCount: null,
  disableReviewsIfZero: false,
  onClick: () => {},
  hideRatingsIfHDPPSku: false,
  targetURL: null
};

ProductDetailsRatings.dataModel = dataModel;

export default ProductDetailsRatings;
